import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Folder } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { api } from "@/lib/api";

/**
 * Fetches the per-run folder summary in a single POST and renders a tiny
 * "📁 N" pill with a hover/tap tooltip listing the folder names.
 *
 * Visibility-filtered server-side — viewers won't see private folders that
 * belong to other users.
 */
export function useFolderSummary(runIds) {
  const ids = Array.isArray(runIds) ? runIds : [];
  const sortedKey = [...ids].sort().join("|");
  return useQuery({
    queryKey: ["folder-summary", sortedKey],
    queryFn: async () => {
      if (!ids.length) return {};
      return (
        await api.post("/folders/by-runs", { run_ids: ids })
      ).data;
    },
    enabled: ids.length > 0,
    staleTime: 30_000,
  });
}

/**
 * R2-Phase-4 — parallel hook for workflow-folder membership.
 * Uses the same shape as `useFolderSummary`: {workflow_id: {count, names}}.
 */
export function useFolderSummaryByWorkflows(workflowIds) {
  const ids = Array.isArray(workflowIds) ? workflowIds : [];
  const sortedKey = [...ids].sort().join("|");
  return useQuery({
    queryKey: ["folder-summary-by-workflow", sortedKey],
    queryFn: async () => {
      if (!ids.length) return {};
      return (
        await api.post("/folders/by-workflows", { workflow_ids: ids })
      ).data;
    },
    enabled: ids.length > 0,
    staleTime: 30_000,
  });
}

/**
 * Inline badge for a single row.  Expects `summary` to be the entry
 * from `useFolderSummary` (or `useFolderSummaryByWorkflows`) for this
 * id (i.e. {count, names}).  Renders an em-dash when count == 0.
 *
 * `runId` is kept as the prop name for backwards-compat; pass either a
 * run_id or workflow_id — it's only used in data-testid suffixes.
 */
export default function FolderCountBadge({ summary, runId }) {
  const count = summary?.count ?? 0;
  if (count === 0) {
    return <span className="text-[10px] text-slate-300" data-testid={`folder-badge-empty-${runId}`}>—</span>;
  }
  const names = summary?.names ?? [];
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1 rounded-full border border-indigo-100 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"
            data-testid={`folder-badge-${runId}`}
          >
            <Folder size={10} strokeWidth={2.25} />
            {count}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="center"
          className="max-w-[260px] text-[11px]"
          data-testid={`folder-badge-tooltip-${runId}`}
        >
          <div className="font-semibold text-slate-800">
            In {count} folder{count === 1 ? "" : "s"}
          </div>
          <ul className="mt-1 space-y-0.5">
            {names.map((n) => (
              <li key={n} className="truncate">• {n}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
